import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const StyledBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(2, 12, 27, 0.82);
  backdrop-filter: blur(10px);
  display: grid;
  place-items: center;
  padding: 16px;
`;

const StyledDialog = styled.div`
  ${({ theme }) => theme.mixins.boxShadow};
  width: min(1200px, 96vw);
  height: min(92vh, 900px);
  background: var(--light-navy);
  border-radius: var(--border-radius);
  border: 1px solid var(--lightest-navy);
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;

  header {
    ${({ theme }) => theme.mixins.flexBetween};
    gap: 16px;
    padding: 14px 14px 12px;
    background: linear-gradient(to bottom, rgba(10, 25, 47, 0.95), rgba(10, 25, 47, 0.7));
    border-bottom: 1px solid rgba(136, 146, 176, 0.18);
  }

  h3 {
    margin: 0;
    color: var(--lightest-slate);
    font-size: clamp(16px, 2.5vw, 20px);
    line-height: 1.25;
  }

  .close {
    ${({ theme }) => theme.mixins.smallButton};
    padding: 10px 12px;
    background-color: transparent;
    color: var(--green);
  }

  .frame {
    width: 100%;
    height: 100%;
    background: var(--navy);
  }

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
`;

const IframeModal = ({ isOpen, title, onClose, src, ariaLabel }) => {
  const dialogRef = useRef(null);
  const lastActiveRef = useRef(null);

  const portalTarget = useMemo(() => {
    if (typeof document === 'undefined') {return null;}
    return document.body;
  }, []);

  useEffect(() => {
    if (!isOpen) {return;}
    if (typeof document === 'undefined') {return;}

    lastActiveRef.current = document.activeElement;
    const onKeyDown = e => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    setTimeout(() => dialogRef.current?.focus?.(), 0);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      lastActiveRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen || !portalTarget) {
    return null;
  }

  return createPortal(
    <StyledBackdrop
      role="presentation"
      onMouseDown={e => {
        if (e.target === e.currentTarget) {onClose?.();}
      }}>
      <StyledDialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || title || 'Game'}
        tabIndex={-1}>
        <header>
          <h3>{title}</h3>
          <button className="close" type="button" onClick={onClose} aria-label="Close game">
            Close game
          </button>
        </header>
        <div className="frame">
          <iframe
            src={src}
            title={title || 'Game'}
            loading="lazy"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            referrerPolicy="no-referrer"
          />
        </div>
      </StyledDialog>
    </StyledBackdrop>,
    portalTarget,
  );
};

IframeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string,
  ariaLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  src: PropTypes.string,
};

export default IframeModal;
